import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ShoppingCart, RefreshCw, Download, Search, 
  CreditCard, CheckCircle2, Trash2, XCircle, Clock, Archive,
  Database, Wifi, WifiOff, Building2, Wallet, Plus, X
} from 'lucide-react';

const VentasPage = () => {
  const { empresaActual } = useApp();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ventas, setVentas] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState([]);
  
  // Configs de Odoo
  const [odooConfigs, setOdooConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('all');
  const [connectionStatus, setConnectionStatus] = useState({});
  
  // Cuentas para pagos
  const [cuentas, setCuentas] = useState([]);
  
  // Filtros - Fechas por defecto: ayer
  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  };
  
  const [fechaDesde, setFechaDesde] = useState(getYesterday);
  const [fechaHasta, setFechaHasta] = useState(getYesterday);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('all');
  
  // Tab de estado del sistema
  const [tabEstado, setTabEstado] = useState('PENDIENTE');
  
  // Selección múltiple
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  
  // Paginación
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  
  // Modal sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncFechaDesde, setSyncFechaDesde] = useState(getYesterday);
  const [syncFechaHasta, setSyncFechaHasta] = useState(getYesterday);
  
  // Modal gestionar pagos temporales
  const [pagosModalOpen, setPagosModalOpen] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [pagosTemp, setPagosTemp] = useState([]);
  const [nuevosPagos, setNuevosPagos] = useState([{ cuenta_id: '', monto: '', nota: '' }]);
  const [loadingPagos, setLoadingPagos] = useState(false);
  
  // Modal ver pagos confirmados
  const [pagosConfirmadosModal, setPagosConfirmadosModal] = useState(false);
  const [pagosConfirmados, setPagosConfirmados] = useState([]);
  
  // Confirmar eliminar pago
  const [pagoEliminar, setPagoEliminar] = useState(null);
  
  // Confirmar acciones masivas
  const [confirmarAccion, setConfirmarAccion] = useState(null);

  // Cargar configs de Odoo y cuentas
  useEffect(() => {
    const loadData = async () => {
      try {
        const [configsRes, cuentasRes] = await Promise.all([
          api.get('/odoo/configs'),
          api.get('/cuentas', { params: { empresa_id: empresaActual?.id } })
        ]);
        setOdooConfigs(configsRes.data);
        setCuentas(cuentasRes.data || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    if (empresaActual?.id) loadData();
  }, [empresaActual?.id]);

  // Cargar ventas
  const cargarVentas = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const params = {
        empresa_id: empresaActual.id,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        estado_sistema: tabEstado,
        limit: pageSize,
        offset: page * pageSize
      };
      
      if (selectedConfig && selectedConfig !== 'all') {
        params.config_key = selectedConfig;
      }
      if (filtroTipo && filtroTipo !== 'all') {
        params.filtro_tipo = filtroTipo;
      }
      
      const [ventasRes, resumenRes] = await Promise.all([
        api.get('/ventas', { params }),
        api.get('/ventas/resumen', { params: {
          empresa_id: empresaActual.id,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          ...(selectedConfig && selectedConfig !== 'all' ? { config_key: selectedConfig } : {})
        }})
      ]);
      
      setVentas(ventasRes.data.ventas);
      setTotal(ventasRes.data.total);
      setResumen(resumenRes.data);
      setSeleccionadas(new Set()); // Limpiar selección al cambiar
    } catch (error) {
      toast.error('Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, fechaDesde, fechaHasta, selectedConfig, filtroTipo, tabEstado, page, pageSize]);

  useEffect(() => {
    cargarVentas();
  }, [cargarVentas]);

  // Probar conexión Odoo
  const testConnection = async (configKey) => {
    setConnectionStatus(prev => ({ ...prev, [configKey]: 'testing' }));
    try {
      const res = await api.post(`/odoo/test-connection/${configKey}`);
      if (res.data.success) {
        setConnectionStatus(prev => ({ 
          ...prev, 
          [configKey]: 'connected',
          [`${configKey}_info`]: res.data
        }));
        toast.success(`Conexión exitosa: ${res.data.user?.name || configKey}`);
      } else {
        setConnectionStatus(prev => ({ ...prev, [configKey]: 'error' }));
        toast.error(`Error: ${res.data.error}`);
      }
    } catch (error) {
      setConnectionStatus(prev => ({ ...prev, [configKey]: 'error' }));
      toast.error(`Error de conexión: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Abrir modal de sincronización
  const openSyncModal = (configKey) => {
    setSyncConfig(configKey || odooConfigs[0]?.key);
    setSyncFechaDesde(getYesterday());
    setSyncFechaHasta(getYesterday());
    setSyncModalOpen(true);
  };

  // Sincronizar ventas
  const handleSync = async () => {
    if (!syncConfig || !empresaActual?.id) return;
    
    setSyncing(true);
    try {
      const res = await api.post(`/odoo/sync/${syncConfig}`, null, {
        params: {
          empresa_id: empresaActual.id,
          fecha_desde: syncFechaDesde,
          fecha_hasta: syncFechaHasta
        }
      });
      
      toast.success(`Sincronización completada: ${res.data.imported} nuevos, ${res.data.updated} actualizados`);
      setSyncModalOpen(false);
      setFechaDesde(syncFechaDesde);
      setFechaHasta(syncFechaHasta);
      cargarVentas();
    } catch (error) {
      toast.error(`Error en sincronización: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // =============================================
  // GESTIÓN DE PAGOS TEMPORALES (ANTES DE CONFIRMAR)
  // =============================================
  
  // Abrir modal de pagos temporales
  const abrirModalPagos = async (venta) => {
    setVentaSeleccionada(venta);
    setPagosModalOpen(true);
    setLoadingPagos(true);
    
    try {
      const res = await api.get(`/ventas/${venta.id}/pagos-temp`);
      setPagosTemp(res.data.pagos);
      
      // Calcular saldo pendiente
      const totalPagado = res.data.pagos.reduce((sum, p) => sum + (p.monto || 0), 0);
      const saldoPendiente = (venta.amount_total || 0) - totalPagado;
      
      // Generar referencia con número de comprobante
      const referencia = venta.pos_reference || venta.name || `Venta #${venta.id}`;
      
      // Por defecto el monto es el saldo pendiente
      setNuevosPagos([{ 
        cuenta_id: '', 
        monto: saldoPendiente > 0 ? saldoPendiente.toFixed(2) : '', 
        nota: referencia 
      }]);
    } catch (error) {
      toast.error('Error al cargar pagos');
      setPagosTemp([]);
      // Aún así inicializar con el total
      const referencia = venta.pos_reference || venta.name || `Venta #${venta.id}`;
      setNuevosPagos([{ 
        cuenta_id: '', 
        monto: (venta.amount_total || 0).toFixed(2), 
        nota: referencia 
      }]);
    } finally {
      setLoadingPagos(false);
    }
  };
  
  // Abrir modal de pagos confirmados (para ventas ya confirmadas)
  const abrirModalPagosConfirmados = async (venta) => {
    setVentaSeleccionada(venta);
    setPagosConfirmadosModal(true);
    setLoadingPagos(true);
    
    try {
      const res = await api.get(`/ventas/${venta.id}/pagos`);
      setPagosConfirmados(res.data.pagos);
    } catch (error) {
      toast.error('Error al cargar pagos');
      setPagosConfirmados([]);
    } finally {
      setLoadingPagos(false);
    }
  };

  // Agregar línea de pago con saldo pendiente
  const agregarLineaPago = () => {
    // Calcular total ya ingresado en nuevos pagos
    const totalNuevosPagos = nuevosPagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
    // Calcular total pagado anteriormente (pagos temp existentes)
    const totalPagosTemp = pagosTemp.reduce((sum, p) => sum + (p.monto || 0), 0);
    // Calcular saldo pendiente
    const totalVenta = ventaSeleccionada?.amount_total || 0;
    const saldoPendiente = totalVenta - totalPagosTemp - totalNuevosPagos;
    
    // Generar referencia
    const referencia = ventaSeleccionada?.pos_reference || ventaSeleccionada?.name || `Venta #${ventaSeleccionada?.id}`;
    
    setNuevosPagos([...nuevosPagos, { 
      cuenta_id: '', 
      monto: saldoPendiente > 0 ? saldoPendiente.toFixed(2) : '', 
      nota: referencia 
    }]);
  };

  // Eliminar línea de pago
  const eliminarLineaPago = (index) => {
    if (nuevosPagos.length > 1) {
      setNuevosPagos(nuevosPagos.filter((_, i) => i !== index));
    }
  };

  // Actualizar línea de pago
  const actualizarLineaPago = (index, campo, valor) => {
    const nuevos = [...nuevosPagos];
    nuevos[index][campo] = valor;
    
    // Si cambió el monto, recalcular el saldo para la siguiente línea vacía
    if (campo === 'monto' && index < nuevos.length - 1) {
      const totalNuevosPagos = nuevos.slice(0, index + 1).reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
      const totalPagosTemp = pagosTemp.reduce((sum, p) => sum + (p.monto || 0), 0);
      const totalVenta = ventaSeleccionada?.amount_total || 0;
      const saldoPendiente = totalVenta - totalPagosTemp - totalNuevosPagos;
      
      // Actualizar la siguiente línea si su monto está vacío o es mayor al saldo
      if (nuevos[index + 1] && (!nuevos[index + 1].monto || parseFloat(nuevos[index + 1].monto) > saldoPendiente)) {
        nuevos[index + 1].monto = saldoPendiente > 0 ? saldoPendiente.toFixed(2) : '';
      }
    }
    
    setNuevosPagos(nuevos);
  };

  // Guardar pagos temporales
  const guardarPagosTemp = async () => {
    const pagosValidos = nuevosPagos.filter(p => p.cuenta_id && parseFloat(p.monto) > 0);
    if (pagosValidos.length === 0) {
      toast.error('Agrega al menos un pago válido');
      return;
    }
    
    setLoadingPagos(true);
    try {
      for (const pago of pagosValidos) {
        await api.post(`/ventas/${ventaSeleccionada.id}/pagos-temp`, {
          cuenta_id: parseInt(pago.cuenta_id),
          monto: parseFloat(pago.monto),
          nota: pago.nota
        });
      }
      toast.success('Pagos asignados correctamente');
      setNuevosPagos([{ cuenta_id: '', monto: '', nota: '' }]);
      // Recargar pagos temp
      const res = await api.get(`/ventas/${ventaSeleccionada.id}/pagos-temp`);
      setPagosTemp(res.data.pagos);
      cargarVentas();
    } catch (error) {
      toast.error('Error al guardar pagos');
    } finally {
      setLoadingPagos(false);
    }
  };
  
  // Eliminar pago temporal
  const eliminarPagoTemp = async (pagoId) => {
    try {
      await api.delete(`/ventas/pagos-temp/${pagoId}`);
      toast.success('Pago eliminado');
      const res = await api.get(`/ventas/${ventaSeleccionada.id}/pagos-temp`);
      setPagosTemp(res.data.pagos);
      cargarVentas();
    } catch (error) {
      toast.error('Error al eliminar pago');
    }
  };
  
  // =============================================
  // ACCIONES DE ESTADO
  // =============================================
  
  // Cambiar estado de una venta
  const cambiarEstadoVenta = async (ventaId, estado) => {
    try {
      await api.put(`/ventas/${ventaId}/estado`, { estado });
      toast.success(`Venta marcada como ${estado}`);
      cargarVentas();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al cambiar estado';
      toast.error(errorMsg);
    }
  };
  
  // Confirmar ventas seleccionadas
  const confirmarVentasSeleccionadas = async () => {
    if (seleccionadas.size === 0) {
      toast.error('Selecciona al menos una venta');
      return;
    }
    
    try {
      const res = await api.post('/ventas/confirmar', {
        venta_ids: Array.from(seleccionadas),
        empresa_id: empresaActual.id
      });
      
      if (res.data.errores?.length > 0) {
        toast.warning(`${res.data.confirmadas} confirmadas, ${res.data.errores.length} con errores`);
      } else {
        toast.success(`${res.data.confirmadas} ventas confirmadas, ${res.data.pagos_creados} pagos creados`);
      }
      setSeleccionadas(new Set());
      setConfirmarAccion(null);
      cargarVentas();
    } catch (error) {
      toast.error('Error al confirmar ventas');
    }
  };
  
  // Descartar ventas seleccionadas
  const descartarVentasSeleccionadas = async () => {
    if (seleccionadas.size === 0) {
      toast.error('Selecciona al menos una venta');
      return;
    }
    
    try {
      await api.post('/ventas/descartar-masivo', {
        venta_ids: Array.from(seleccionadas)
      });
      toast.success(`${seleccionadas.size} ventas descartadas`);
      setSeleccionadas(new Set());
      setConfirmarAccion(null);
      cargarVentas();
    } catch (error) {
      toast.error('Error al descartar ventas');
    }
  };
  
  // Eliminar descartadas permanentemente
  const eliminarDescartadas = async () => {
    try {
      const res = await api.delete(`/ventas/descartadas?empresa_id=${empresaActual.id}`);
      toast.success(`${res.data.eliminadas} ventas eliminadas permanentemente`);
      setConfirmarAccion(null);
      cargarVentas();
    } catch (error) {
      toast.error('Error al eliminar ventas');
    }
  };
  
  // Toggle selección
  const toggleSeleccion = (ventaId) => {
    const nuevas = new Set(seleccionadas);
    if (nuevas.has(ventaId)) {
      nuevas.delete(ventaId);
    } else {
      nuevas.add(ventaId);
    }
    setSeleccionadas(nuevas);
  };
  
  // Seleccionar todas
  const seleccionarTodas = () => {
    if (seleccionadas.size === ventas.length) {
      setSeleccionadas(new Set());
    } else {
      setSeleccionadas(new Set(ventas.map(v => v.id)));
    }
  };

  // =============================================
  // LEGACY: Crear pagos (para compatibilidad)
  // =============================================
  
  // Crear pagos (ahora usa pagos temp)
  const crearPagos = async () => {
    await guardarPagosTemp();
  };
  
  const [pagosVenta, setPagosVenta] = useState([]);
  const [saldoPendiente, setSaldoPendiente] = useState(0);
  
  const handleCrearPagos = async () => {
    const pagosValidos = nuevosPagos.filter(p => p.cuenta_id && parseFloat(p.monto) > 0);
    if (pagosValidos.length === 0) {
      toast.error('Agrega al menos un pago válido');
      return;
    }
    
    const totalNuevos = pagosValidos.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
    if (totalNuevos > saldoPendiente + 0.01) {
      toast.error(`El total (${totalNuevos.toFixed(2)}) excede el saldo pendiente (${saldoPendiente.toFixed(2)})`);
      return;
    }
    
    setLoadingPagos(true);
    try {
      await api.post(`/ventas/${ventaSeleccionada.id}/pagos`, {
        pagos: pagosValidos.map(p => ({
          cuenta_id: parseInt(p.cuenta_id),
          monto: parseFloat(p.monto),
          nota: p.nota || null
        }))
      });
      
      toast.success('Pagos creados correctamente');
      
      // Recargar pagos
      const res = await api.get(`/ventas/${ventaSeleccionada.id}/pagos`);
      setPagosVenta(res.data.pagos);
      setSaldoPendiente(res.data.saldo_pendiente);
      setNuevosPagos([{ cuenta_id: '', monto: '', nota: '' }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear pagos');
    } finally {
      setLoadingPagos(false);
    }
  };

  // Eliminar pago
  const confirmarEliminarPago = async () => {
    if (!pagoEliminar) return;
    
    try {
      await api.delete(`/ventas/${ventaSeleccionada.id}/pagos/${pagoEliminar.id}`);
      toast.success('Pago eliminado');
      
      // Recargar
      const res = await api.get(`/ventas/${ventaSeleccionada.id}/pagos`);
      setPagosVenta(res.data.pagos);
      setSaldoPendiente(res.data.saldo_pendiente);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pago');
    } finally {
      setPagoEliminar(null);
    }
  };

  // Formatear monto
  const formatMonto = (monto) => {
    const num = parseFloat(monto) || 0;
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Formatear fecha y hora
  const formatFechaHora = (dateStr) => {
    if (!dateStr) return { fecha: '-', hora: '-' };
    try {
      const d = new Date(dateStr);
      return {
        fecha: d.toLocaleDateString('es-PE'),
        hora: d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
      };
    } catch {
      return { fecha: dateStr.split(' ')[0], hora: dateStr.split(' ')[1] || '-' };
    }
  };

  // Filtrar ventas por búsqueda
  const ventasFiltradas = ventas.filter(v => {
    if (!busqueda) return true;
    const search = busqueda.toLowerCase();
    return (
      v.name?.toLowerCase().includes(search) ||
      v.num_comp?.toLowerCase().includes(search) ||
      v.partner_name?.toLowerCase().includes(search) ||
      v.vendedor_name?.toLowerCase().includes(search) ||
      v.tienda_name?.toLowerCase().includes(search) ||
      v.company_name?.toLowerCase().includes(search) ||
      String(v.odoo_id).includes(search)
    );
  });

  // Calcular totales
  const totalMonto = resumen.reduce((sum, r) => sum + (r.monto_total || 0), 0);
  const totalVentas = resumen.reduce((sum, r) => sum + (r.total_ventas || 0), 0);
  
  // Total de nuevos pagos
  const totalNuevosPagos = nuevosPagos.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);

  return (
    <div className="space-y-6" data-testid="ventas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-indigo-600" />
            Ventas POS
          </h1>
          <p className="text-gray-500 text-sm mt-1">Importadas desde Odoo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cargarVentas} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button 
            onClick={() => openSyncModal(selectedConfig !== 'all' ? selectedConfig : null)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Sincronizar Odoo
          </Button>
        </div>
      </div>

      {/* Configs de Odoo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {odooConfigs.map((config) => (
          <Card key={config.key} className={`cursor-pointer transition-all ${
            selectedConfig === config.key ? 'ring-2 ring-indigo-500 bg-indigo-50/50' : 'hover:bg-gray-50'
          }`} onClick={() => setSelectedConfig(config.key)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    connectionStatus[config.key] === 'connected' ? 'bg-emerald-100' :
                    connectionStatus[config.key] === 'error' ? 'bg-red-100' :
                    'bg-gray-100'
                  }`}>
                    {connectionStatus[config.key] === 'connected' ? (
                      <Wifi className="w-5 h-5 text-emerald-600" />
                    ) : connectionStatus[config.key] === 'error' ? (
                      <WifiOff className="w-5 h-5 text-red-600" />
                    ) : (
                      <Database className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{config.nombre}</h3>
                    <p className="text-sm text-gray-500">{config.username}</p>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    testConnection(config.key);
                  }}
                  disabled={connectionStatus[config.key] === 'testing'}
                >
                  {connectionStatus[config.key] === 'testing' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : 'Probar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm">Total Ventas</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700">{totalVentas.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm">Monto Total</span>
            </div>
            <p className="text-xl font-bold text-emerald-700">S/. {formatMonto(totalMonto)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Buscar</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="ID, nombre, comprobante, cliente..." 
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Empresa Odoo</Label>
              <Select value={selectedConfig} onValueChange={setSelectedConfig}>
                <SelectTrigger className="w-[180px] mt-1">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {odooConfigs.map(c => (
                    <SelectItem key={c.key} value={c.key}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-[180px] mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ventas_reales">Ventas Reales</SelectItem>
                  <SelectItem value="creditos">Créditos</SelectItem>
                  <SelectItem value="contado">Al Contado</SelectItem>
                  <SelectItem value="reservas_pendientes">Reservas Pendientes</SelectItem>
                  <SelectItem value="reservas_facturadas">Reservas Facturadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="mt-1" />
            </div>
            <Button onClick={cargarVentas} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de Ventas con Tabs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Ventas ({total.toLocaleString()} registros)</CardTitle>
            <div className="flex items-center gap-2">
              {tabEstado === 'PENDIENTE' && seleccionadas.size > 0 && (
                <>
                  <Button 
                    size="sm" 
                    onClick={() => setConfirmarAccion('confirmar')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Confirmar ({seleccionadas.size})
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setConfirmarAccion('descartar')}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Descartar ({seleccionadas.size})
                  </Button>
                </>
              )}
              {tabEstado === 'DESCARTADA' && ventas.length > 0 && (
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => setConfirmarAccion('eliminar')}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Eliminar todas
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={tabEstado} onValueChange={(v) => { setTabEstado(v); setPage(0); }} className="w-full">
            <div className="border-b px-4">
              <TabsList className="bg-transparent h-auto p-0 gap-4">
                <TabsTrigger 
                  value="PENDIENTE" 
                  className="px-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-transparent"
                >
                  <Clock className="w-4 h-4 mr-2 text-amber-500" />
                  Pendientes
                </TabsTrigger>
                <TabsTrigger 
                  value="CONFIRMADA" 
                  className="px-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 data-[state=active]:bg-transparent"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                  Confirmadas
                </TabsTrigger>
                <TabsTrigger 
                  value="CREDITO" 
                  className="px-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent"
                >
                  <CreditCard className="w-4 h-4 mr-2 text-blue-500" />
                  Crédito
                </TabsTrigger>
                <TabsTrigger 
                  value="DESCARTADA" 
                  className="px-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-gray-500 data-[state=active]:bg-transparent"
                >
                  <Archive className="w-4 h-4 mr-2 text-gray-500" />
                  Descartadas
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value={tabEstado} className="m-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {tabEstado === 'PENDIENTE' && (
                        <TableHead className="w-[40px]">
                          <Checkbox 
                            checked={seleccionadas.size === ventas.length && ventas.length > 0}
                            onCheckedChange={seleccionarTodas}
                          />
                        </TableHead>
                      )}
                      <TableHead className="w-[60px]">ID</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>N° Comprobante</TableHead>
                      <TableHead>Orden</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tienda</TableHead>
                      <TableHead>Pagos Odoo</TableHead>
                      {tabEstado === 'PENDIENTE' && <TableHead>Pagos Asignados</TableHead>}
                      {tabEstado === 'CONFIRMADA' && <TableHead>Pagos</TableHead>}
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center py-8 text-gray-400">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          Cargando ventas...
                        </TableCell>
                      </TableRow>
                    ) : ventasFiltradas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center py-8 text-gray-400">
                          <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          No hay ventas {tabEstado === 'PENDIENTE' ? 'pendientes' : tabEstado === 'CONFIRMADA' ? 'confirmadas' : tabEstado === 'CREDITO' ? 'a crédito' : 'descartadas'}
                        </TableCell>
                      </TableRow>
                    ) : ventasFiltradas.map((v) => {
                      const { fecha, hora } = formatFechaHora(v.date_order);
                      return (
                        <TableRow key={v.id} className={`hover:bg-gray-50 ${seleccionadas.has(v.id) ? 'bg-amber-50' : ''}`}>
                          {tabEstado === 'PENDIENTE' && (
                            <TableCell>
                              <Checkbox 
                                checked={seleccionadas.has(v.id)}
                                onCheckedChange={() => toggleSeleccion(v.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-xs text-gray-500">
                            {v.odoo_id}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{fecha}</div>
                            <div className="text-xs text-gray-400">{hora}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              v.tipo_comp === 'BV' ? 'bg-green-100 text-green-700' :
                              v.tipo_comp === 'FA' ? 'bg-blue-100 text-blue-700' :
                              v.tipo_comp === 'NV' ? 'bg-gray-100 text-gray-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {v.tipo_comp || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {v.num_comp || '-'}
                          </TableCell>
                          <TableCell className="font-mono text-sm font-medium text-indigo-600">
                            {v.name}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 text-gray-400" />
                              <span className="truncate max-w-[100px]" title={v.company_name}>
                                {v.company_name || '-'}
                              </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate" title={v.partner_name}>
                        {v.partner_name || <span className="text-gray-400">-</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {v.tienda_name || '-'}
                      </TableCell>
                      <TableCell className="text-xs max-w-[120px]">
                        <Badge variant="outline" className="font-normal">
                          {v.x_pagos || '-'}
                        </Badge>
                      </TableCell>
                      {/* Columna de Pagos Asignados (solo PENDIENTE) */}
                      {tabEstado === 'PENDIENTE' && (
                        <TableCell>
                          {v.count_pagos_temp > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              {v.count_pagos_temp} pago{v.count_pagos_temp > 1 ? 's' : ''} - S/.{formatMonto(v.total_pagos_temp)}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin asignar</span>
                          )}
                        </TableCell>
                      )}
                      {/* Columna de Pagos (solo CONFIRMADA) */}
                      {tabEstado === 'CONFIRMADA' && (
                        <TableCell>
                          {v.total_pagos > 0 ? (
                            <Badge className="bg-green-100 text-green-700">
                              S/.{formatMonto(v.total_pagos)}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono font-bold text-emerald-600">
                        S/. {formatMonto(v.amount_total)}
                      </TableCell>
                      <TableCell className="text-center">
                        {/* Acciones según el tab */}
                        {tabEstado === 'PENDIENTE' && (
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => abrirModalPagos(v)}
                              className="gap-1"
                            >
                              <Wallet className="w-4 h-4" />
                              {v.count_pagos_temp > 0 ? 'Editar' : 'Asignar'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => cambiarEstadoVenta(v.id, 'CREDITO')}
                              title="Marcar como Crédito"
                            >
                              <CreditCard className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => cambiarEstadoVenta(v.id, 'DESCARTADA')}
                              title="Descartar"
                            >
                              <XCircle className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                        {tabEstado === 'CONFIRMADA' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => abrirModalPagosConfirmados(v)}
                          >
                            <Wallet className="w-4 h-4 mr-1" />
                            Ver Pagos
                          </Button>
                        )}
                        {tabEstado === 'CREDITO' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => cambiarEstadoVenta(v.id, 'PENDIENTE')}
                            title="Volver a Pendientes"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                        )}
                        {tabEstado === 'DESCARTADA' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => cambiarEstadoVenta(v.id, 'PENDIENTE')}
                            title="Restaurar"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Paginación */}
          {total > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-500">
                Página {page + 1} de {Math.ceil(total / pageSize)}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Anterior
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * pageSize >= total}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* AlertDialog para confirmar acciones masivas */}
      <AlertDialog open={confirmarAccion !== null} onOpenChange={(open) => !open && setConfirmarAccion(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmarAccion === 'confirmar' && '¿Confirmar ventas seleccionadas?'}
              {confirmarAccion === 'descartar' && '¿Descartar ventas seleccionadas?'}
              {confirmarAccion === 'eliminar' && '¿Eliminar permanentemente todas las descartadas?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmarAccion === 'confirmar' && (
                <>
                  Se confirmarán {seleccionadas.size} ventas y se crearán los pagos correspondientes.
                  <br /><strong>Nota:</strong> Solo se confirmarán las que tienen pagos asignados.
                </>
              )}
              {confirmarAccion === 'descartar' && (
                <>Se marcarán {seleccionadas.size} ventas como descartadas. Podrás restaurarlas después.</>
              )}
              {confirmarAccion === 'eliminar' && (
                <>Esta acción es irreversible. Se eliminarán todas las ventas descartadas permanentemente.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmarAccion === 'confirmar') confirmarVentasSeleccionadas();
                else if (confirmarAccion === 'descartar') descartarVentasSeleccionadas();
                else if (confirmarAccion === 'eliminar') eliminarDescartadas();
              }}
              className={confirmarAccion === 'eliminar' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmarAccion === 'confirmar' && 'Confirmar'}
              {confirmarAccion === 'descartar' && 'Descartar'}
              {confirmarAccion === 'eliminar' && 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Sincronizar */}
      <Dialog open={syncModalOpen} onOpenChange={setSyncModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-indigo-600" />
              Sincronizar Ventas desde Odoo
            </DialogTitle>
            <DialogDescription>
              Importar ventas POS del rango de fechas seleccionado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Empresa Odoo</Label>
              <Select value={syncConfig || ''} onValueChange={setSyncConfig}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {odooConfigs.map(c => (
                    <SelectItem key={c.key} value={c.key}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={syncFechaDesde} onChange={(e) => setSyncFechaDesde(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={syncFechaHasta} onChange={(e) => setSyncFechaHasta(e.target.value)} className="mt-1" />
              </div>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p>Las ventas existentes se actualizarán (no se duplicarán).</p>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setSyncModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSync} disabled={syncing || !syncConfig} className="bg-indigo-600 hover:bg-indigo-700">
              {syncing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sincronizando...</> : <><Download className="w-4 h-4 mr-2" /> Sincronizar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Gestionar Pagos Temporales (antes de confirmar) */}
      <Dialog open={pagosModalOpen} onOpenChange={setPagosModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" />
              Asignar Pagos (Pre-confirmación)
            </DialogTitle>
            <DialogDescription>
              Asigna las cuentas donde recibirás el pago. Los pagos reales se crearán al confirmar.
            </DialogDescription>
          </DialogHeader>
          
          {ventaSeleccionada && (
            <div className="space-y-4 mt-4">
              {/* Info de la venta */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Venta:</span>
                  <span className="font-semibold">{ventaSeleccionada.pos_reference || ventaSeleccionada.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-bold text-lg text-emerald-600">S/. {formatMonto(ventaSeleccionada.amount_total)}</span>
                </div>
                {pagosTemp.length > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ya Asignado:</span>
                      <span className="font-semibold text-amber-600">S/. {formatMonto(pagosTemp.reduce((sum, p) => sum + p.monto, 0))}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600 font-medium">Saldo Pendiente:</span>
                      <span className="font-bold text-lg text-red-600">
                        S/. {formatMonto(ventaSeleccionada.amount_total - pagosTemp.reduce((sum, p) => sum + p.monto, 0))}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Método Odoo:</span>
                  <Badge variant="secondary">{ventaSeleccionada.x_pagos || 'No especificado'}</Badge>
                </div>
              </div>
              
              {/* Pagos temporales existentes */}
              {pagosTemp.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    Pagos Asignados (Pendientes de confirmar)
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cuenta</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagosTemp.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="font-medium">{p.cuenta_nombre}</TableCell>
                            <TableCell className="text-right font-mono">S/. {formatMonto(p.monto)}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => eliminarPagoTemp(p.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-amber-50">
                          <TableCell className="font-semibold">Total Asignado</TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            S/. {formatMonto(pagosTemp.reduce((sum, p) => sum + p.monto, 0))}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Agregar nuevos pagos */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar Pagos
                </h4>
                <div className="space-y-2">
                  {nuevosPagos.map((pago, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <Select value={pago.cuenta_id} onValueChange={(v) => actualizarLineaPago(idx, 'cuenta_id', v)}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Seleccionar cuenta" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentas.map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.nombre} ({c.moneda_codigo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder="Monto"
                        className="w-32"
                        value={pago.monto}
                        onChange={(e) => actualizarLineaPago(idx, 'monto', e.target.value)}
                      />
                      <Input
                        placeholder="Nota (opc)"
                        className="w-40"
                        value={pago.nota}
                        onChange={(e) => actualizarLineaPago(idx, 'nota', e.target.value)}
                      />
                      {nuevosPagos.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => eliminarLineaPago(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={agregarLineaPago}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar línea
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPagosModalOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={guardarPagosTemp} disabled={loadingPagos}>
              {loadingPagos ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Guardar Asignaciones
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar pago */}
      <AlertDialog open={!!pagoEliminar} onOpenChange={() => setPagoEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pago?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el pago de S/. {formatMonto(pagoEliminar?.monto_me)} en {pagoEliminar?.cuenta_nombre}. 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarEliminarPago} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VentasPage;
